import { useTranslation } from 'react-i18next';
import { AlertTriangle, Infinity as InfinityIcon } from 'lucide-react';
import { Card } from '@components/atoms/ui-kit/card.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { CustomModelAllowance } from '@utils/api/subscriptions/use-current-custom-model-allowance.tsx';

interface Props {
  allowance: CustomModelAllowance | undefined;
  loading: boolean;
}

/**
 * "3 of 4 rounds used this year · €99/round". Sits next to
 * :class:`ReadinessCard` on the custom-model page (#2211 criterion 5).
 *
 * Amber when the remaining count reaches 1, red when the allowance is
 * exhausted; the Train button on the page reads the same allowance and
 * disables itself with the reason stated inline (not in a tooltip), per
 * the ticket. Null ``limit`` means the plan is unlimited — the card
 * degrades to "3 rounds this year · Unlimited" rather than showing a
 * useless fraction.
 */
export const RoundAllowanceCard = ({ allowance, loading }: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  if (loading && !allowance) {
    return (
      <Card padding="none" className="p-4">
        <Text as="p" size="sm" tone="muted">
          {t('round-allowance-loading')}
        </Text>
      </Card>
    );
  }
  if (!allowance) return null;

  const { used, limit, remaining, unitCost } = allowance;
  const isUnlimited = limit == null;
  const exhausted = !isUnlimited && (remaining ?? 0) <= 0;
  const nearLimit = !isUnlimited && !exhausted && (remaining ?? 0) <= 1;

  // Near-limit and exhausted both use danger; the ``AlertTriangle`` on
  // exhausted is what visually distinguishes them. There is no dedicated
  // warning token in the theme, and inventing one is out of scope.
  const accent =
    exhausted || nearLimit ? getColour('danger') : getColour('activeText');

  return (
    <Card padding="none" className="p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {exhausted && (
            <AlertTriangle
              className="h-4 w-4"
              style={{ color: accent }}
              aria-hidden="true"
            />
          )}
          {isUnlimited && (
            <InfinityIcon
              className="h-4 w-4"
              style={{ color: accent }}
              aria-hidden="true"
            />
          )}
          <Text as="span" size="sm" weight="semibold">
            {t('round-allowance-title')}
          </Text>
        </div>
        {unitCost != null && unitCost > 0 && (
          <Text as="span" size="xs" tone="muted">
            {t('round-allowance-cost', { cost: formatCost(unitCost) })}
          </Text>
        )}
      </div>

      <div className="flex items-baseline gap-2">
        <Text as="span" size="xl" weight="semibold" style={{ color: accent }}>
          {isUnlimited
            ? String(used)
            : t('round-allowance-fraction', { used, limit })}
        </Text>
        <Text as="span" size="xs" tone="muted">
          {isUnlimited
            ? t('round-allowance-unlimited-suffix')
            : t('round-allowance-remaining', {
                remaining: Math.max(0, remaining ?? 0)
              })}
        </Text>
      </div>

      {exhausted && (
        <Text as="p" size="xs" style={{ color: getColour('danger') }}>
          {t('round-allowance-exhausted-help')}
        </Text>
      )}
      {nearLimit && !exhausted && (
        <Text as="p" size="xs" tone="muted">
          {t('round-allowance-near-limit-help')}
        </Text>
      )}
    </Card>
  );
};

const formatCost = (n: number): string => {
  // Panel-wide currency is EUR today; if a plan uses another currency in
  // the future the API can move currency onto the response and this stays
  // the last place that formats it. Two decimals unless the value is a
  // whole number, in which case skip them for legibility.
  if (Number.isInteger(n)) return `€${n}`;
  return `€${n.toFixed(2)}`;
};
